// Slide data for IMPACT presentation
// Each slide has: id, type, title, content, and optional visual configuration

export const slidesData = [
  {
    id: 1,
    type: 'title',
    title: 'IMPACT',
    subtitle: 'How Proof-of-Impact Will Rewire the Global Economy',
    footer: 'optio-logo',
    audioSrc: '/audio/slide_1.mp3'
  },
  {
    id: 2,
    type: 'convergence-intro',
    title: 'Blockchain + AI: Where Are We Going?',
    subtitle: 'The Convergence of Two Transformational Technologies',
    pillars: [
      {
        name: 'Blockchain',
        role: 'provides trust.',
        description: 'A decentralized ledger that records events, ownership, and transactions without centralized control.',
        insight: 'Records what happened.'
      },
      {
        name: 'Artificial Intelligence',
        role: 'provides intelligence.',
        description: 'Systems that analyze massive data streams to discover patterns, verify outcomes, and guide decisions.',
        insight: 'Evaluates what it means.'
      }
    ],
    result: 'Verifiable human contribution.',
    measures: ['Learning', 'Environmental Restoration', 'Health Improvement', 'Knowledge Creation', 'Community Cooperation'],
    evolution: [
      { era: 'First Internet', verb: 'moved', object: 'information' },
      { era: 'Second Internet', verb: 'captured', object: 'attention' },
      { era: 'Next Internet', verb: 'will measure', object: 'impact' }
    ],
    audioSrc: '/audio/slide_2.mp3'
  },
  {
    id: 3,
    type: 'blockchain-markets',
    title: '$867 Trillion in Markets Are Open to Blockchain',
    attribution: '— World Economic Forum',
    assets: ['Stocks', 'Bonds', 'Commodities', 'Real Estate', 'Financial Contracts', 'Global Trade Assets'],
    capabilities: [
      'Tokenization of real-world assets',
      'Transparent and verifiable transactions',
      'Global digital marketplaces',
      'Programmable financial systems'
    ],
    biggerPicture: 'Blockchain is not just about cryptocurrency. It is the infrastructure layer for the next global financial system.',
    flow: ['Traditional Assets', 'Tokenized Assets', 'Global Blockchain Networks'],
    audioSrc: '/audio/slide_3.mp3'
  },
  {
    id: 4,
    type: 'blockchain-evolution',
    title: 'The Next Evolution of Blockchain',
    phases: [
      {
        label: 'Phase 1',
        name: 'Tokenization',
        description: 'Blockchain begins by digitizing assets.',
        items: ['Money (Bitcoin, Stablecoins)', 'Stocks & Bonds', 'Real Estate', 'Commodities', 'Financial Contracts'],
        outcome: 'Traditional assets move onto blockchain networks.'
      },
      {
        label: 'Phase 2',
        name: 'Proof-of-Impact',
        description: 'Blockchain expands beyond financial assets. It begins verifying real-world outcomes:',
        items: ['Learning achievements', 'Environmental restoration', 'Health improvements', 'Knowledge creation', 'Community contributions'],
        outcome: 'Human contribution becomes measurable.'
      },
      {
        label: 'Phase 3',
        name: 'The Impact Economy',
        description: 'When impact becomes measurable, economic incentives change. Capital begins flowing toward:',
        items: ['Education', 'Innovation', 'Environmental restoration', 'Public health', 'Community development'],
        outcome: 'The economy begins rewarding positive human actions.'
      }
    ],
    closing: ['Blockchain digitized assets.', 'Proof-of-Impact digitizes human contribution.'],
    audioSrc: '/audio/slide_4.mp3'
  },
  {
    id: 5,
    type: 'impact-equation-detailed',
    title: 'The Impact Equation',
    subtitle: 'The Formula for Measuring Human Contribution',
    formula: 'Impact = (Action x Quality) x Verification x Network',
    terms: [
      {
        name: 'Action',
        description: 'A human activity that attempts to improve something.',
        examples: 'Teaching, restoring land, publishing knowledge.'
      },
      {
        name: 'Quality',
        description: 'How effective the action actually is.',
        examples: 'Better outcomes create stronger impact.'
      },
      {
        name: 'Verification',
        description: 'Trusted systems confirm the improvement occurred.',
        examples: 'AI, sensors, blockchain, and peer validation.'
      },
      {
        name: 'Network',
        description: 'When verified improvements spread through large systems, the impact multiplies.',
        examples: 'Global reach amplifies proven results.'
      }
    ],
    example: {
      steps: [
        { text: 'A teacher explains a concept to one student.', label: 'Action x Quality', result: 'learning improvement' },
        { text: 'A platform verifies the learning outcome.', label: 'Verification', result: 'trusted proof' },
        { text: 'The lesson spreads to 10,000 students.', label: 'Network', result: 'massive impact' }
      ]
    },
    insight: 'Impact grows when meaningful actions are verified and amplified through networks.',
    bottomLine: ['Blockchain records what happened.', 'AI evaluates what it means.', 'Together they enable the measurement of human impact.'],
    audioSrc: '/audio/slide_5.mp3'
  },
  {
    id: 6,
    type: 'impact-flywheel-detailed',
    title: 'The Impact Flywheel',
    subtitle: 'How the Impact Economy Scales',
    flowSummary: 'Action → Impact → Reward → Participation → More Action',
    steps: [
      {
        name: 'Action',
        description: 'People take actions that improve the world.',
        items: ['Teaching knowledge', 'Restoring ecosystems', 'Improving health', 'Building useful technology']
      },
      {
        name: 'Impact',
        description: 'Systems verify the improvement. Proof-of-Impact confirms that a real outcome occurred.',
        items: []
      },
      {
        name: 'Reward',
        description: 'Verified impact generates recognition and incentives.',
        items: ['Economic rewards', 'Reputation signals', 'Funding', 'Opportunities']
      },
      {
        name: 'Participation',
        description: 'When people see that meaningful contributions are rewarded, more people join the system.',
        items: []
      },
      {
        name: 'More Action',
        description: 'More participants generate more improvements, which feed back into the system. The flywheel accelerates.',
        items: []
      }
    ],
    insight: 'When positive human actions are measured and rewarded, progress becomes self-reinforcing.',
    closing: ['The attention economy went viral because engagement was rewarded.', 'The Impact Economy grows when improvement is rewarded.'],
    audioSrc: '/audio/slide_6.mp3'
  },
  {
    id: 7,
    type: 'impact-stack-detailed',
    title: 'The Impact Stack',
    subtitle: 'The Technology Architecture of the Impact Economy',
    layers: [
      {
        num: 5,
        name: 'Markets',
        description: 'Capital flows toward verified impact.',
        items: ['Impact investments', 'Outcome-based funding', 'Tokenized impact assets']
      },
      {
        num: 4,
        name: 'Incentives',
        description: 'Systems reward verified contributions.',
        items: ['Economic rewards', 'Reputation systems', 'Access to opportunities']
      },
      {
        num: 3,
        name: 'Verification',
        description: 'Technology confirms real-world outcomes.',
        items: ['AI analysis', 'Sensors & IoT', 'Peer validation', 'Blockchain records']
      },
      {
        num: 2,
        name: 'Data',
        description: 'Actions generate measurable signals.',
        items: ['Learning platforms', 'Health systems', 'Environmental monitoring', 'Knowledge networks']
      },
      {
        num: 1,
        name: 'Identity',
        description: 'Trusted digital identity links actions to people and organizations.',
        items: ['Decentralized identity', 'Credentials', 'Verifiable reputation']
      }
    ],
    insight: 'When identity, data, verification, incentives, and markets connect, human contribution becomes measurable and valuable.',
    closing: ['Blockchain records the ledger. AI verifies outcomes.', 'The Impact Stack turns human contribution into economic value.'],
    audioSrc: '/audio/slide_7.mp3'
  },
  {
    id: 8,
    type: 'oracle-apps',
    title: 'Integrated Oracle Apps',
    subtitle: 'Parler became the first fully integrated Oracle App ecosystem on the Optio Blockchain, setting the blueprint for all future integrations.',
    apps: [
      {
        name: 'Parler',
        logo: '/logos/Parler.svg',
        logoBg: '#ef4444',
        status: 'Live',
        description: 'The flagship social platform — first fully integrated Oracle App on Optio Blockchain.',
        features: ['On-chain engagement validation', 'Daily token distribution', 'Identity wallet integration'],
        caseStudy: true
      },
      {
        name: 'Core',
        logo: '/logos/Core.png',
        logoBg: '#1a1a2e',
        status: 'Live',
        description: 'The SOVREN Data Lake — decentralized data infrastructure powering the Optio ecosystem.',
        features: ['Decentralized data storage', 'Cross-app data layer', 'Sovereign data ownership'],
        caseStudy: false
      },
      {
        name: 'Echo',
        logo: '/logos/Echo.png',
        logoBg: '#dc2626',
        status: 'Live',
        description: 'P2P end-to-end encrypted chat with on-chain identity verification.',
        features: ['End-to-end encryption', 'P2P messaging', 'On-chain identity'],
        caseStudy: false
      },
      {
        name: 'Pay',
        logo: '/logos/Pay.png',
        logoBg: '#22c55e',
        status: 'Live',
        description: 'Native payment rails enabling seamless OPT transactions across the ecosystem.',
        features: ['Instant payments', 'Cross-app transactions', 'Merchant integration'],
        caseStudy: false
      },
      {
        name: 'Play',
        logo: '/logos/Play.png',
        logoBg: '#8b5cf6',
        status: 'Live',
        description: 'Streaming and entertainment with transparent royalty distribution to creators.',
        features: ['Streaming royalties', 'Creator economics', 'Viewer engagement rewards'],
        caseStudy: false
      },
      {
        name: 'Shop',
        logo: '/logos/Shop.png',
        logoBg: '#eab308',
        status: 'Live',
        description: 'Decentralized marketplace with blockchain-verified transactions and seller reputation.',
        features: ['Transaction verification', 'Seller reputation on-chain', 'Token rewards for purchases'],
        caseStudy: false
      },
      {
        name: 'Parler Cloud',
        logo: '/logos/Cloud.svg',
        logoBg: '#0d9488',
        status: 'Live',
        description: 'Decentralized storage and compute powered by the Optio node network.',
        features: ['Distributed storage', 'Node operator rewards', 'Data sovereignty'],
        caseStudy: false
      },
      {
        name: 'Parler News',
        logo: '/logos/News.svg',
        logoBg: '#3b82f6',
        status: 'Live',
        description: 'Citizen journalism platform with immutable content attribution and creator rewards.',
        features: ['Content attribution', 'Creator token rewards', 'Engagement validation'],
        caseStudy: false
      },
      {
        name: 'Studio',
        logo: '/logos/Studio.png',
        logoBg: '#d946ef',
        status: 'Live',
        description: 'Manage all your social with one app.',
        features: ['Multi-platform management', 'Unified dashboard', 'Content scheduling'],
        caseStudy: false
      },
      {
        name: 'Sovren Node',
        logo: '/logos/Node.svg',
        logoBg: '#111111',
        status: 'Live',
        description: 'Sovren Blockchain infrastructure node.',
        features: ['Blockchain validation', 'Network security', 'Decentralized infrastructure'],
        caseStudy: false
      },
      {
        name: 'VU',
        logo: '/logos/VU.svg',
        logoBg: '#ec4899',
        status: 'Live',
        description: 'Non-political social media platform.',
        features: ['Community-focused', 'Non-political content', 'User-first experience'],
        caseStudy: false
      }
    ],
    audioSrc: '/audio/slide_8.mp3'
  },
  {
    id: 9,
    type: 'big-idea',
    headline: 'The next economic revolution will measure human contribution.',
    cards: [
      {
        icon: 'verify',
        title: 'Verified Actions',
        description: 'Every meaningful contribution is cryptographically recorded and verified'
      },
      {
        icon: 'reward',
        title: 'Direct Rewards',
        description: 'Value flows directly to those who create positive outcomes'
      },
      {
        icon: 'network',
        title: 'Global Network',
        description: 'A borderless system connecting contributors worldwide'
      },
      {
        icon: 'measure',
        title: 'Impact Measurement',
        description: 'AI and sensors quantify real-world improvements'
      }
    ],
    audioSrc: '/audio/slide_9.mp3'
  },
  {
    id: 10,
    type: 'evolution-pyramid',
    title: 'Every Economy Measures Something',
    subtitle: 'The evolution of value creation',
    levels: [
      { label: 'Impact', era: 'Tomorrow', highlight: true, description: 'Verified contribution' },
      { label: 'Attention', era: '2010s', highlight: false, description: 'Clicks, views, engagement' },
      { label: 'Knowledge', era: '1990s', highlight: false, description: 'Information & expertise' },
      { label: 'Labor', era: '1900s', highlight: false, description: 'Time & physical work' },
      { label: 'Extraction', era: 'Ancient', highlight: false, description: 'Raw materials & resources' }
    ],
    audioSrc: '/audio/slide_10.mp3'
  },
  {
    id: 11,
    type: 'attention-economy',
    title: 'The Attention Economy',
    subtitle: "Today's internet monetizes attention",
    metrics: [
      { icon: 'heart', label: 'Likes', value: '4.7B', subtext: 'daily' },
      { icon: 'share', label: 'Shares', value: '1.2B', subtext: 'daily' },
      { icon: 'cursor', label: 'Clicks', value: '12B', subtext: 'daily' },
      { icon: 'clock', label: 'Watch Time', value: '1B+', subtext: 'hours/day' }
    ],
    audioSrc: '/audio/slide_11.mp3'
  },
  {
    id: 12,
    type: 'hidden-incentive',
    title: 'The Hidden Incentive',
    headline: 'Algorithms optimize for engagement, not improvement',
    loop: [
      { label: 'Content', icon: 'content' },
      { label: 'Engagement', icon: 'engagement' },
      { label: 'Algorithm', icon: 'algorithm' },
      { label: 'Amplify', icon: 'amplify' }
    ],
    insight: 'The system rewards what spreads, not what matters',
    audioSrc: '/audio/slide_12.mp3'
  },
  {
    id: 13,
    type: 'impact-paradox',
    title: 'The Impact Paradox',
    headline: 'The content that spreads fastest is rarely the content that improves society most.',
    comparison: {
      left: {
        label: 'What Spreads',
        color: '#f43f5e',
        items: ['Outrage', 'Conflict', 'Fear', 'Division']
      },
      right: {
        label: 'What Improves',
        color: '#0068ff',
        items: ['Education', 'Nuance', 'Solutions', 'Unity']
      }
    },
    audioSrc: '/audio/slide_13.mp3'
  },
  {
    id: 14,
    type: 'big-question',
    headline: 'What if we could measure human contribution?',
    subtext: 'Every act of learning, healing, creating, teaching, restoring...',
    audioSrc: '/audio/slide_14.mp3'
  },
  {
    id: 15,
    type: 'breakthrough',
    title: 'The Breakthrough',
    subtitle: 'Three technologies converging to make verification possible',
    technologies: [
      { name: 'Blockchain', icon: 'blockchain', description: 'Immutable record of every contribution', color: '#8b5cf6' },
      { name: 'Sensors', icon: 'sensors', description: 'Real-world data capture at scale', color: '#06b6d4' },
      { name: 'Artificial Intelligence', icon: 'ai', description: 'Pattern recognition & verification', color: '#0068ff' }
    ],
    audioSrc: '/audio/slide_15.mp3'
  },
  {
    id: 16,
    type: 'sales-hero',
    title: 'Own a Piece of the Infrastructure Powering the Next Internet',
    product: 'Optio Decentralized Cloud Node',
    description: 'Own and operate part of the infrastructure powering the Optio Cloud.',
    tagline: 'This is your opportunity to own software infrastructure helping power the next evolution of technology.',
    callout: 'Some are calling it the next industrial revolution.',
    cta: 'Buy Optio Node',
    audioSrc: '/audio/slide_16.mp3'
  },
  {
    id: 17,
    type: 'sales-awareness',
    title: 'Everyone Is Talking About Blockchain and AI',
    subtitle: 'Blockchain and AI are dominating global technology headlines.',
    problems: ['How to participate', 'Where the real opportunity is', 'What actually creates value'],
    punchline: 'You want a clear business model.',
    audioSrc: '/audio/slide_17.mp3'
  },
  {
    id: 18,
    type: 'sales-challenge',
    title: 'The Challenge',
    subtitle: 'Entering this space can feel overwhelming.',
    concerns: [
      { name: 'Risk Everywhere', description: 'Many crypto projects fail.' },
      { name: 'New Territory', description: 'Blockchain and AI are new concepts for many people.' },
      { name: 'Tech Barrier', description: "Most people don't want to become engineers just to participate." }
    ],
    punchline: 'You want something simple and logical.',
    audioSrc: '/audio/slide_18.mp3'
  },
  {
    id: 19,
    type: 'sales-solution',
    title: 'A Simple Way to Participate',
    subtitle: 'Instead of trading tokens or chasing speculation...',
    solution: 'You can own infrastructure.',
    description: 'Infrastructure powers the entire digital economy. Every application needs:',
    items: ['Compute', 'Storage', 'Networking', 'Processing power'],
    closing: 'Optio Nodes help provide that capacity.',
    audioSrc: '/audio/slide_19.mp3'
  },
  {
    id: 20,
    type: 'sales-comparison',
    title: 'Think of It Like Owning an Apartment — But in the Cloud',
    left: {
      label: 'Rental Property',
      rows: [
        { metric: 'Upfront cost', value: '$50K – $200K+' },
        { metric: 'Monthly costs', value: '$500 – $2,000+' },
        { metric: 'Tenant management', value: 'Required' },
        { metric: 'Maintenance', value: 'Ongoing' },
        { metric: 'Market risk', value: 'Local / physical' }
      ]
    },
    right: {
      label: 'Optio Node',
      rows: [
        { metric: 'Upfront cost', value: '$999' },
        { metric: 'Monthly costs', value: '$0' },
        { metric: 'Tenant management', value: 'None' },
        { metric: 'Maintenance', value: 'None' },
        { metric: 'Market risk', value: 'Global / digital' }
      ]
    },
    closing: 'You own digital infrastructure that applications use. You earn network rewards.',
    audioSrc: '/audio/slide_20.mp3'
  },
  {
    id: 21,
    type: 'sales-whatisnode',
    title: 'What Is an Optio Node?',
    subtitle: 'An Optio Node is dedicated, partitioned hardware that runs secure virtual machines connected to the Optio Cloud.',
    functions: [
      { name: 'Run Core Infrastructure', description: 'Operate virtual machines that power blockchain services and cloud operations.' },
      { name: 'Process Application Workloads', description: 'Handle analytics, data processing, and network operations.' },
      { name: 'Expand Platform Capacity', description: 'Provide compute, storage, and networking resources.' },
      { name: 'Ensure Isolation & Performance', description: 'Secure partitioning keeps workloads stable and scalable.' }
    ],
    audioSrc: '/audio/slide_21.mp3'
  },
  {
    id: 22,
    type: 'sales-demand',
    title: 'Why Cloud Infrastructure Demand Keeps Growing',
    subtitle: 'Every major digital service depends on cloud infrastructure.',
    metrics: [
      { label: 'Streaming', value: '+340%' },
      { label: 'Payments', value: '+180%' },
      { label: 'AI Tools', value: '+890%' },
      { label: 'Business Software', value: '+210%' },
      { label: 'Social Platforms', value: '+150%' },
      { label: 'Data Storage', value: '+420%' }
    ],
    closing: 'Just like growing cities need more apartments... the internet needs more infrastructure.',
    audioSrc: '/audio/slide_22.mp3'
  },
  {
    id: 23,
    type: 'sales-rewards',
    title: 'How Node Owners Are Rewarded',
    subtitle: 'As the Optio Cloud grows:',
    growth: ['More applications run on it', 'More users generate activity', 'More infrastructure is required'],
    rewards: ['Contributing capacity', 'Supporting network expansion', 'Holding licensed node positions'],
    closing: 'Rewards are distributed daily. Similar to collecting rent from digital infrastructure.',
    audioSrc: '/audio/slide_23.mp3'
  },
  {
    id: 24,
    type: 'sales-apps',
    title: 'Apps Drive Demand',
    subtitle: 'Applications integrate with Optio for one reason: user engagement.',
    description: 'Apps reward behavior through the network.',
    apps: ['Parler Social', 'Parler Pay', 'Parler Cloud', 'Parler Shop', 'Parler Play', 'Parler News'],
    flywheel: ['User activity grows', 'Reward distribution grows', 'Infrastructure demand grows'],
    closing: 'Node owners sit directly in the middle of that demand.',
    audioSrc: '/audio/slide_24.mp3'
  },
  {
    id: 25,
    type: 'sales-loyalty',
    title: 'Airline Miles — But for Apps',
    subtitle: 'Airlines created loyalty programs like SkyMiles to increase engagement. Apps integrate with Optio for the same reason.',
    left: {
      label: 'Traditional App Loyalty',
      items: ['Coupons or points', 'Company absorbs cost', 'Limited upside']
    },
    right: {
      label: 'Optio-Powered Loyalty',
      items: ['Rewards tied to real activity', 'Shared infrastructure', 'Engagement compounds', 'Demand for infrastructure grows']
    },
    audioSrc: '/audio/slide_25.mp3'
  },
  {
    id: 26,
    type: 'sales-outlook',
    title: '10-Year Outlook: Cloud Infrastructure Demand',
    stats: [
      { value: '$2.39T', label: 'Cloud spending projected by 2030' },
      { value: '1,587 TWh', label: 'Data center power demand by 2030' },
      { value: '50%', label: 'Of US power-demand growth from AI workloads' }
    ],
    punchline: 'Cloud revenue expected to triple over the next decade.',
    closing: 'The cloud is not shrinking. It is expanding dramatically.',
    audioSrc: '/audio/slide_26.mp3'
  },
  {
    id: 27,
    type: 'sales-early',
    title: 'Why Early Adoption Matters',
    benefits: [
      { name: 'First mover advantage', description: 'Early infrastructure owners benefit the most.' },
      { name: 'Capacity rewards', description: 'Infrastructure providers earn rewards as usage grows.' },
      { name: 'Limited supply', description: 'Node licenses are limited. Early buyers secure the best positions.' }
    ],
    audioSrc: '/audio/slide_27.mp3'
  },
  {
    id: 28,
    type: 'sales-pricing',
    title: 'Optio Node Pricing',
    tiers: [
      { range: '1–25 Nodes', price: '$999', discount: null },
      { range: '26–50 Nodes', price: '$899', discount: '10% discount' },
      { range: '51–100 Nodes', price: '$799', discount: '20% discount' }
    ],
    examples: [
      { qty: '1 Node', total: '$999' },
      { qty: '10 Nodes', total: '$9,990' },
      { qty: '25 Nodes', total: '$24,975' }
    ],
    audioSrc: '/audio/slide_28.mp3'
  },
  {
    id: 29,
    type: 'sales-audience',
    title: 'Who Optio Nodes Are Designed For',
    personas: [
      { name: 'Growth seekers', description: 'People who understand infrastructure demand.' },
      { name: 'Opportunity seekers', description: 'People who see long-term technology trends.' },
      { name: 'Simplicity lovers', description: 'People who want participation without technical complexity.' }
    ],
    closing: 'No technical background required.',
    audioSrc: '/audio/slide_29.mp3'
  },
  {
    id: 30,
    type: 'sales-cta',
    title: 'Take Action Today',
    subtitle: 'Demand for digital infrastructure continues to grow.',
    points: ['Applications expand.', 'User activity compounds.', 'Infrastructure capacity becomes more valuable.'],
    message: 'You now have the opportunity to own part of that infrastructure.',
    actions: ['Own a Node', 'Advance the Cloud', 'Earn Rewards'],
    cta: 'Buy Optio Node',
    audioSrc: '/audio/slide_30.mp3'
  }
];

export default slidesData;
